import { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  fetchTractGeoJSON,
  augmentFeatures,
  buildColorExpression,
  formatValue,
  getGeoJSONBounds
} from '../utils/mapUtils';
import LoadingSpinner from './LoadingSpinner';
import ErrorBanner from './ErrorBanner';

function MapLegend({ legendData, gradient }) {
  if (!legendData || !gradient) return null;
  const { minVal, maxVal, unit, label } = legendData;
  return (
    <div className="map-legend">
      <div className="map-legend-label">{label}</div>
      <div
        className="map-legend-gradient"
        style={{ background: `linear-gradient(to right, ${gradient.light}, ${gradient.dark})` }}
      />
      <div className="map-legend-range">
        <span>{formatValue(minVal, unit)}</span>
        <span>{formatValue(maxVal, unit)}</span>
      </div>
      <div className="map-legend-nodata">
        <span className="map-legend-nodata-swatch" />
        No data
      </div>
    </div>
  );
}

// Choropleth map that also shows engagement submission pins.
// In pickerMode, clicking the map drops/moves a draggable marker instead of
// showing popups, and onPickerChange is called with [lng, lat].
export default function EngagementMap({
  state,
  county,
  geography,
  indicatorConfig,
  gradient,
  submissions,
  indicators,
  pickerMode,
  pickerLocation,
  onPickerChange
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const pickerMarker = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [geoJSON, setGeoJSON] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [legendData, setLegendData] = useState(null);
  const [fitDone, setFitDone] = useState(false);

  // Use refs for values read inside Mapbox event handlers to avoid stale closures.
  const indicatorsRef = useRef(indicators);
  useEffect(() => { indicatorsRef.current = indicators; }, [indicators]);

  const pickerModeRef = useRef(pickerMode);
  useEffect(() => { pickerModeRef.current = pickerMode; }, [pickerMode]);

  const onPickerChangeRef = useRef(onPickerChange);
  useEffect(() => { onPickerChangeRef.current = onPickerChange; }, [onPickerChange]);

  // Fetch TIGER tract boundaries
  useEffect(() => {
    if (!state || !county) return;
    let cancelled = false;
    setGeoJSON(null);
    setGeoLoading(true);
    setGeoError(null);
    setFitDone(false);
    fetchTractGeoJSON(state, county)
      .then(gj => { if (!cancelled) { setGeoJSON(gj); setGeoLoading(false); } })
      .catch(err => { if (!cancelled) { setGeoError(err.message); setGeoLoading(false); } });
    return () => { cancelled = true; };
  }, [state, county]);

  // Initialise Mapbox instance — runs once per mount
  useEffect(() => {
    if (!mapContainer.current) return;
    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [0, 0],
      zoom: 1,
      accessToken: process.env.REACT_APP_MAPBOX_TOKEN
    });
    m.addControl(new mapboxgl.NavigationControl(), 'top-right');
    popup.current = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
    m.on('load', () => { map.current = m; setMapLoaded(true); });
    return () => {
      setMapLoaded(false);
      map.current = null;
      popup.current?.remove();
      pickerMarker.current?.remove();
      pickerMarker.current = null;
      m.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fit bounds once after GeoJSON and map are both ready
  useEffect(() => {
    if (!mapLoaded || !geoJSON || !map.current || fitDone) return;
    const bounds = getGeoJSONBounds(geoJSON);
    if (bounds) {
      map.current.fitBounds(bounds, { padding: 40, animate: false });
      setFitDone(true);
    }
  }, [mapLoaded, geoJSON, fitDone]);

  // Update choropleth when data changes
  useEffect(() => {
    if (!mapLoaded || !geoJSON || !map.current) return;

    const augmented = augmentFeatures(geoJSON, geography ?? []);
    const values = (geography ?? []).map(g => g.value).filter(v => v !== null);
    const minVal = values.length ? Math.min(...values) : 0;
    const maxVal = values.length && Math.max(...values) !== Math.min(...values)
      ? Math.max(...values)
      : (values.length ? Math.min(...values) + 1 : 1);

    const activeGradient = gradient ?? { light: '#d4edba', dark: '#2d6b49' };
    const colorExpr = buildColorExpression(minVal, maxVal, activeGradient);

    setLegendData({
      minVal, maxVal,
      unit: indicatorConfig?.unit,
      label: indicatorConfig?.label
    });

    if (!map.current.getSource('tracts')) {
      map.current.addSource('tracts', { type: 'geojson', data: augmented });
      map.current.addLayer({
        id: 'tracts-fill',
        type: 'fill',
        source: 'tracts',
        paint: { 'fill-color': colorExpr, 'fill-opacity': 0.75 }
      });
      map.current.addLayer({
        id: 'tracts-outline',
        type: 'line',
        source: 'tracts',
        paint: { 'line-color': '#ffffff', 'line-width': 0.5 }
      });
    } else {
      map.current.getSource('tracts').setData(augmented);
      map.current.setPaintProperty('tracts-fill', 'fill-color', colorExpr);
    }
  }, [mapLoaded, geoJSON, geography, indicatorConfig, gradient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Submissions circle layer
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const pinFeatures = (submissions ?? [])
      .filter(s => s.has_location && s.location_lng != null && s.location_lat != null)
      .map(s => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.location_lng, s.location_lat] },
        properties: {
          id: s.id,
          connection_type: s.connection_type ?? '',
          indicator_id: s.indicator_id ?? '',
          question: s.question ?? '',
          created_at: s.created_at ?? ''
        }
      }));

    const geojsonData = { type: 'FeatureCollection', features: pinFeatures };

    if (!map.current.getSource('submissions')) {
      map.current.addSource('submissions', { type: 'geojson', data: geojsonData });
      map.current.addLayer({
        id: 'submissions-circles',
        type: 'circle',
        source: 'submissions',
        paint: {
          'circle-radius': 8,
          'circle-color': '#c8614a',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      map.current.on('click', 'submissions-circles', (e) => {
        if (pickerModeRef.current) return;
        const props = e.features[0].properties;
        const outcomeLabel = indicatorsRef.current?.find(i => i.id === props.indicator_id)?.label
          ?? props.indicator_id;
        const date = props.created_at
          ? new Date(props.created_at).toLocaleDateString()
          : '';
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<strong>${props.connection_type || 'Community member'}</strong>` +
            (props.indicator_id ? `<br/><em>${outcomeLabel}</em>` : '') +
            `<br/>${props.question}` +
            (date ? `<br/><small>${date}</small>` : '')
          )
          .addTo(map.current);
        e.originalEvent.stopPropagation();
      });

      map.current.on('mouseenter', 'submissions-circles', () => {
        if (!pickerModeRef.current) map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'submissions-circles', () => {
        if (!pickerModeRef.current) map.current.getCanvas().style.cursor = '';
      });
    } else {
      map.current.getSource('submissions').setData(geojsonData);
    }
  }, [mapLoaded, submissions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picker mode — click to place/move a draggable marker
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    if (!pickerMode) {
      map.current.getCanvas().style.cursor = '';
      return;
    }

    map.current.getCanvas().style.cursor = 'crosshair';

    function handleMapClick(e) {
      const { lng, lat } = e.lngLat;
      if (pickerMarker.current) {
        pickerMarker.current.setLngLat([lng, lat]);
      } else {
        pickerMarker.current = new mapboxgl.Marker({ draggable: true, color: '#c8614a' })
          .setLngLat([lng, lat])
          .addTo(map.current);
        pickerMarker.current.on('dragend', () => {
          const pos = pickerMarker.current.getLngLat();
          onPickerChangeRef.current?.([pos.lng, pos.lat]);
        });
      }
      onPickerChangeRef.current?.([lng, lat]);
    }

    map.current.on('click', handleMapClick);
    return () => { map.current?.off('click', handleMapClick); };
  }, [mapLoaded, pickerMode]);

  // Remove the picker marker when pickerLocation is cleared externally
  useEffect(() => {
    if (!pickerLocation) {
      pickerMarker.current?.remove();
      pickerMarker.current = null;
    }
  }, [pickerLocation]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      {indicatorConfig && legendData && (
        <MapLegend legendData={legendData} gradient={gradient ?? { light: '#d4edba', dark: '#2d6b49' }} />
      )}
      {geoLoading && (
        <div className="map-overlay">
          <LoadingSpinner label="Loading boundaries…" />
        </div>
      )}
      {geoError && (
        <div className="map-overlay map-overlay--error">
          <ErrorBanner message={`Boundaries failed to load: ${geoError}`} />
        </div>
      )}
    </div>
  );
}
